import React from 'react'
import { OptionRectangle } from '../../atoms/OptionRectangle'
import { Safety, EmailFeedbackSuccess, Chat } from '@olxbr/ds-spots'
import { AlertCircle, ChevronRight } from '@olxbr/ds-icons'
import { Container, Link } from '@olxbr/ds-web'
import { Tooltip } from '../../atoms/Tooltip'
import { View } from '../../atoms/View'
import styled from 'styled-components'
import HelpFooter from '../../molecules/HelpFooter'
import { HelpModalTemplate } from '../HelpModalTemplate'
import PropTypes from 'prop-types'
import TermsAndPrivacyNotice from '../../molecules/TermsAndPrivacyNotice'
import EmailChip from '../../atoms/EmailChip'
import useWindowSize from '../../../util/useWindowSize'
import { isLoginScrollEnabled } from '../../../util/experiments'
import { breakpoints } from '../../../util/medias'
import { Avatar } from '@olxbr/ds-core'

const DESKTOP_BP = `${breakpoints.tablet + 1}px`

const getContainerElement = ({ modalMode, ...props }) =>
  modalMode ? <View {...props} /> : <Container {...props} />

const ContentWrapper = styled(View)`
  ${({ modalMode }) => !modalMode && `margin-top: var(--spacing-6)`};
`

const ContainerWrapper = styled(getContainerElement)`
  && {
    padding: 0;
    overflow: visible;
  }
`

const FullWidth = styled.div`
  width: 100%;
`

const FooterWrapper = styled(View)`
  display: flex;
  justify-content: center;
  max-width: 466px;
  margin: auto;
  margin-top: var(--spacing-1);
  text-align: center;
  overflow: hidden;
`

const OptionParent = styled.div`
  display: flex;
  padding-bottom: var(--spacing-1);
  width: 100%;
`

export const loginMethodEmailAriaLabel = 'selecionar método de login via email'
export const loginMethodPasswordAriaLabel = 'selecionar método de login via senha'
export const loginMethodFacialRecognitionAriaLabel =
  'selecionar método de login via reconhecimento facial'
export const loginMethodBackAriaLabel = 'botão para voltar a tela anterior'
export const emailChipAriaLabel = 'email do usuário'

const NewPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 88px);
  @media (min-width: ${DESKTOP_BP}) {
    margin-top: var(--spacing-6);
    min-height: auto;
    display: block;
  }
`

const TwoColumnWrapper = styled.div`
  margin: 0 auto;
  flex: 1;
  @media (min-width: ${DESKTOP_BP}) {
    display: flex;
    align-items: stretch;
    justify-content: space-between;
    max-width: 1040px;
    padding: var(--spacing-6) var(--spacing-4) 0;
    gap: var(--spacing-7);
  }
`

const LeftColumn = styled.div`
  display: none;
  @media (min-width: ${DESKTOP_BP}) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex: 1;
    max-width: 469px;
  }
`

const RightColumn = styled.div`
  @media (min-width: ${DESKTOP_BP}) {
    width: 504px;
    flex-shrink: 0;
  }
`

const NewMobileHeader = styled.div`
  padding: var(--spacing-3) var(--spacing-2) 0;
  margin-botom: var(--spacing-4);
  @media (min-width: ${DESKTOP_BP}) {
    display: none;
  }
`

const NewOptionsWrapper = styled.div`
  padding: var(--spacing-2);
  @media (min-width: ${DESKTOP_BP}) {
    padding: var(--spacing-3) var(--spacing-4);
  }
`

const NewOptionParent = styled.div`
  width: 100%;
  margin-bottom: var(--spacing-1);
`

const NewDesktopFooter = styled.div`
  margin-top: auto;
  padding-top: var(--spacing-7);
`

const NewMobileFooter = styled.div`
  display: flex;
  justify-content: center;
  max-width: 466px;
  margin: auto;
  margin-top: auto;
  text-align: center;
  padding: var(--spacing-1) var(--spacing-2);
  @media (min-width: ${DESKTOP_BP}) {
    display: none;
  }
`

const HideOnMobile = styled.div`
  @media (max-width: ${parseInt(DESKTOP_BP) - 1}px) {
    display: none;
  }
`

const LoginMethodChooserTemplate = ({
  email,
  allowedMethods,
  showHelpingModalLink,
  onBackClick,
  onForgotPassword,
  onReactivationLinkClick,
  onMagicLinkClick,
  onHelpLinkClick,
  onHelpLinkClose,
  onPasswordMethodClick,
  onEmailMethodClick,
  onFacialRecognitionMethodClick,
  onPrivacyLinkClick,
  onTermsLinkClick,
  abTests,
  modalMode,
}) => {
  const [showHelpModal, setShowHelpModal] = React.useState(false)
  const { isMobile } = useWindowSize()

  const getAllowedMethodsContent = methods => [
    {
      title: 'E-mail',
      icon: <EmailFeedbackSuccess shape="circle" size="48" />,
      actionIcon: !methods.EMAIL ? (
        <Tooltip
          text={
            <span className="typo-body-small text-neutral-70">
              A opção{' '}
              <span className="typo-body-small text-neutral-70 font-semibold inline">Email</span>{' '}
              não está disponível no momento.
            </span>
          }
        >
          <AlertCircle />
        </Tooltip>
      ) : (
        <div className="flex" aria-label={loginMethodEmailAriaLabel}>
          <ChevronRight />
        </div>
      ),
      text: 'Enviaremos um código para o seu e-mail',
      disabled: !methods.EMAIL,
      onClick: onEmailMethodClick,
    },
    {
      title: 'Senha',
      icon: <Safety shape="circle" size="48" />,
      actionIcon: !methods.PASSWORD ? (
        <Tooltip
          text={
            <span className="typo-body-small text-neutral-70">
              A opção{' '}
              <span className="typo-body-small text-neutral-70 font-semibold inline">Senha</span>{' '}
              não está disponível no momento.
            </span>
          }
        >
          <AlertCircle />
        </Tooltip>
      ) : (
        <div className="flex" aria-label={loginMethodPasswordAriaLabel}>
          <ChevronRight />
        </div>
      ),
      text: 'Informe a sua senha atual',
      disabled: !methods.PASSWORD,
      onClick: onPasswordMethodClick,
    },
    methods.FACIAL_RECOGNITION
      ? {
          title: 'Reconhecimento facial',
          icon: <Chat shape="circle" size="48" />,
          actionIcon: (
            <div className="flex" aria-label={loginMethodFacialRecognitionAriaLabel}>
              <ChevronRight />
            </div>
          ),
          text: 'Tirar uma foto do rosto',
          onClick: onFacialRecognitionMethodClick,
          disabled: false,
        }
      : null,
  ]

  if (isLoginScrollEnabled(abTests) && !modalMode) {
    return (
      <NewPageWrapper>
        <TwoColumnWrapper>
          <LeftColumn>
            <div>
              <span className="typo-body-large mb-2" style={{ fontSize: '32px' }}>
                Escolha um método de acesso para a conta
              </span>
              <div className="flex items-center gap-1" aria-label={emailChipAriaLabel}>
                <Avatar size="small" />
                <span className="typo-body-medium font-bold">{email}</span>
              </div>
            </div>
            <NewDesktopFooter>
              <TermsAndPrivacyNotice
                onPrivacyClick={onPrivacyLinkClick}
                onTermsClick={onTermsLinkClick}
              />
            </NewDesktopFooter>
          </LeftColumn>
          <RightColumn>
            <NewMobileHeader>
              <span className="typo-title-large mb-0-5">Como deseja acessar?</span>
              <span className="typo-body-medium mb-2">
                Escolha um método de acesso para a conta
              </span>
              <div className="flex items-center gap-1 mb-4" aria-label={emailChipAriaLabel}>
                <Avatar size="small" />
                <span className="typo-body-medium font-bold">{email}</span>
              </div>
            </NewMobileHeader>
            <NewOptionsWrapper>
              {getAllowedMethodsContent(allowedMethods).map(
                method =>
                  method && (
                    <NewOptionParent key={method.title}>
                      <OptionRectangle {...method} modalMode={modalMode} experimentOn>
                        {method.text}
                      </OptionRectangle>
                    </NewOptionParent>
                  )
              )}
              <HideOnMobile>
                <div className="flex justify-center mt-2 mb-2">
                  <Link aria-label={loginMethodBackAriaLabel} size="medium" onClick={onBackClick}>
                    Voltar
                  </Link>
                </div>
              </HideOnMobile>
            </NewOptionsWrapper>
          </RightColumn>
        </TwoColumnWrapper>
        <NewMobileFooter>
          <TermsAndPrivacyNotice
            onPrivacyClick={onPrivacyLinkClick}
            onTermsClick={onTermsLinkClick}
          />
        </NewMobileFooter>
        <HelpModalTemplate
          abTests={abTests}
          open={showHelpModal}
          onForgotPasswordLinkClick={onForgotPassword}
          onReactivationLinkClick={onReactivationLinkClick}
          onMagicLinkClick={onMagicLinkClick}
          onClose={() => {
            setShowHelpModal(false)
            onHelpLinkClose()
          }}
        />
      </NewPageWrapper>
    )
  }

  const renderContent = content =>
    !isMobile ? (
      <ContainerWrapper type="outlined" modalMode={modalMode}>
        {content}
      </ContainerWrapper>
    ) : (
      <div className="flex pl-1 pr-1">{content}</div>
    )

  return (
    <div className="flex justify-center items-center flex-col">
      <ContentWrapper modalMode={modalMode}>
        {renderContent(
          <div className="flex items-center flex-col">
            <div
              className="flex items-center flex-col"
              style={{
                paddingTop: !modalMode && 'var(--spacing-3)',
                paddingBottom: !modalMode && 'var(--spacing-0-25)',
                paddingLeft: !modalMode && !isMobile && 'var(--spacing-7)',
                paddingRight: !modalMode && !isMobile && 'var(--spacing-7)',
              }}
            >
              <span className="typo-title-medium">Acesse sua conta</span>
              <span className="typo-body-medium mt-3 mb-1">
                Escolha um método de acesso para a conta
              </span>
              <div className="flex mb-3" aria-label={emailChipAriaLabel}>
                <EmailChip>{email}</EmailChip>
              </div>
              <div className="flex justify-center items-center flex-col">
                {getAllowedMethodsContent(allowedMethods).map(
                  method =>
                    method && (
                      <OptionParent>
                        <OptionRectangle key={method.title} {...method} modalMode={modalMode}>
                          {method.text}
                        </OptionRectangle>
                      </OptionParent>
                    )
                )}
                <div className="flex mt-1 mb-2">
                  <Link aria-label={loginMethodBackAriaLabel} size="medium" onClick={onBackClick}>
                    Voltar
                  </Link>
                </div>
              </div>
            </div>
            {showHelpingModalLink && (
              <FullWidth>
                <HelpFooter
                  onClick={() => {
                    setShowHelpModal(true)
                    onHelpLinkClick()
                  }}
                />
              </FullWidth>
            )}
          </div>
        )}

        <FooterWrapper>
          <TermsAndPrivacyNotice
            onPrivacyClick={onPrivacyLinkClick}
            onTermsClick={onTermsLinkClick}
          />
        </FooterWrapper>
      </ContentWrapper>
      <HelpModalTemplate
        abTests={abTests}
        open={showHelpModal}
        onForgotPasswordLinkClick={onForgotPassword}
        onReactivationLinkClick={onReactivationLinkClick}
        onMagicLinkClick={onMagicLinkClick}
        onClose={() => {
          setShowHelpModal(false)
          onHelpLinkClose()
        }}
      />
    </div>
  )
}

export { LoginMethodChooserTemplate }

LoginMethodChooserTemplate.propTypes = {
  email: PropTypes.string.isRequired,
  allowedMethods: PropTypes.shape({
    EMAIL: PropTypes.bool,
    PASSWORD: PropTypes.bool,
    FACIAL_RECOGNITION: PropTypes.bool,
  }).isRequired,
  showHelpingModalLink: PropTypes.bool,
  onBackClick: PropTypes.func.isRequired,
  onForgotPassword: PropTypes.func.isRequired,
  onReactivationLinkClick: PropTypes.func.isRequired,
  onMagicLinkClick: PropTypes.func.isRequired,
  onHelpLinkClose: PropTypes.func,
  onHelpLinkClick: PropTypes.func,
  onPasswordMethodClick: PropTypes.func,
  onEmailMethodClick: PropTypes.func,
  onFacialRecognitionMethodClick: PropTypes.func,
  onPrivacyLinkClick: PropTypes.func,
  onTermsLinkClick: PropTypes.func,
  abTests: PropTypes.string,
  modalMode: PropTypes.bool,
}
